import {
  Component,
  OnInit,
  CUSTOM_ELEMENTS_SCHEMA,
  Input,
} from "@angular/core";
import { NgClass, AsyncPipe } from "@angular/common";
import { SvgIconComponent } from "angular-svg-icon";
import { RouterLink, RouterLinkActive, Router } from "@angular/router";
import { distinctUntilChanged, Observable } from "rxjs";
import { NavigationService } from "../../services/navigation.service";
import { FirebaseAuthService } from "../../services/firebase-auth.service";
import { RolesService } from "../../services/roles.service";
import { User } from "@angular/fire/auth";
import { Settings } from "../../utils/settings";

export interface NavigationMain {
  "Category-Icon"?: string | null;
  "Category-Icon-Color-Class"?: string | null;
  "Category-Text"?: string | null;
  "Category-Value"?: string | null;
  "Category-Link-Enabled"?: boolean;
  "Group-Icon"?: string | null;
  "Group-Text"?: string | null;
  "Nav-Item-Text"?: string | null;
  "Badge text"?: string | null;
  Route?: string | null;
  "Description for html meta-tag"?: string | null;
}

@Component({
  selector: "app-nav-aside",
  standalone: true,
  imports: [NgClass, AsyncPipe, SvgIconComponent, RouterLink, RouterLinkActive],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: "./nav-aside.html",
  styleUrls: ["./nav-aside.scss"],
})
export class NavAside implements OnInit {
  navigation: NavigationMain[] = [];
  user$: Observable<User | null>;

  svgPath = "assets/icons/heroicons/optimized/24/outline/";
  @Input() dataUrl!: null | string;
  @Input() pt!: null | string;

  constructor(
    private router: Router,
    private ns: NavigationService,
    private firebaseAuth: FirebaseAuthService,
    public rolesService: RolesService,
  ) {
    this.user$ = this.firebaseAuth.user$;
  }

  ngOnInit(): void {
    // this.debugCategoryStructure();

    // Subscribe to user changes to dynamically load the correct navigation
    this.user$.subscribe((user) => {
      // If user is logged in and we're on a guest navigation URL, switch to authenticated nav
      let navigationUrl = this.dataUrl!;

      if (user && this.dataUrl?.includes("navigation-guest.json")) {
        // User is logged in but we're using guest navigation - switch to authenticated
        navigationUrl = this.dataUrl.replace(
          "navigation-guest.json",
          "navigation.json",
        );
      }

      this.ns
        .getNavigation(navigationUrl)
        .pipe(distinctUntilChanged())
        .subscribe((data) => {
          return (this.navigation = data);
        });
    });
  }

  getSvgPath(iconName: string): string {
    return `${this.svgPath}${iconName}.svg`;
  }

  getSvgIconColorClass(iconColorClass: string): string {
    return `w-5 h-5 ${iconColorClass}`;
  }

  getDisclosureId(categoryText: string | null | undefined): string {
    if (!categoryText) return "disclosure-unknown";
    return `disclosure-${categoryText
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "")}`;
  }

  // Get only items that are at root level (Category-Value === 'root')
  getRootItems(): NavigationMain[] {
    return this.navigation.filter(
      (item) =>
        item["Category-Value"] === "root" || item["Category-Value"] === null,
    );
  }

  // Get children for a specific category by matching Category-Text
  getChildrenForCategory(parentCategoryText: string): NavigationMain[] {
    const categoryMapping: { [key: string]: string } = {
      "Artists & Releases": "artists_releases",
      Management: "management",
      "Social Media": "social_media",
      Generators: "generators",
    };

    const targetCategoryValue = categoryMapping[parentCategoryText];
    if (!targetCategoryValue) return [];
    console.log("targetCategoryValue: ", targetCategoryValue);
    // Return items that have the matching category value AND have nav text
    return this.navigation.filter(
      (item) =>
        item["Category-Value"] === targetCategoryValue && item["Nav-Item-Text"],
    );
  }

  isSeparator(item: NavigationMain): boolean {
    return (
      item["Category-Icon"] === "separator" || item["Nav-Item-Text"] === "-"
    );
  }

  isCategory(item: NavigationMain): boolean {
    return !!(
      item["Category-Icon"] &&
      item["Category-Icon"] !== "separator" &&
      item["Category-Text"] &&
      item["Category-Value"] === "root" &&
      !item["Nav-Item-Text"]
    );
  }

  isTopLevelItem(item: NavigationMain): boolean {
    return !!(
      item["Category-Icon"] &&
      item["Category-Icon"] !== "separator" &&
      item["Nav-Item-Text"] &&
      item["Category-Value"] === "root" &&
      !item["Category-Text"]
    );
  }

  getBadgeClass(badgeText: string | null): string {
    if (!badgeText) return "";

    switch (badgeText.toLowerCase()) {
      case "new":
        return "badge-success";
      case "updated":
        return "badge-info";
      case "endra":
        return "badge-warning";
      default:
        return "badge-neutral";
    }
  }

  isExternalLink(route: string | null | undefined): boolean {
    return !!(
      route &&
      (route.startsWith("http://") || route.startsWith("https://"))
    );
  }

  isFontAwesomeIcon(iconName: string | null | undefined): boolean {
    return !!(iconName && iconName.startsWith("fa fa-"));
  }

  isFontAwesomeBrandIcon(iconName: string | null | undefined): boolean {
    return !!(iconName && iconName.startsWith("fab fa-"));
  }

  isRouteActive(categoryText?: string): boolean {
    if (!categoryText) return false;

    const currentUrl = this.router.url;
    const children = this.getChildrenForCategory(categoryText);

    // Check if any of the category's child routes match the current route
    return children.some((child) => {
      if (!child.Route) return false;

      // Skip external links
      if (this.isExternalLink(child.Route)) return false;

      // Check if current URL starts with the child route
      // Remove leading slash for comparison if needed
      const childRoute = child.Route.startsWith("/")
        ? child.Route
        : "/" + child.Route;
      return (
        currentUrl === childRoute || currentUrl.startsWith(childRoute + "/")
      );
    });
  }

  async onLogout(): Promise<void> {
    console.log("onLogout: ");
    try {
      const result = await this.firebaseAuth.signOut();
      if (result.success) {
        Settings.removeSync("isAuthenticated");
        Settings.removeSync("rememberedEmail");
        this.router.navigate(["/login"]);
      }
    } catch (error) {
      console.error("Logout error:", error);
    }
  }

  onLogin(): void {
    this.router.navigate(["/login"]);
  }

  // private debugCategoryStructure() {
  //   console.log("=== DEBUG CATEGORY STRUCTURE ===");
  //
  //   const rootItems = this.getRootItems();
  //   console.log(
  //     "Root items:",
  //     rootItems.map((item) => ({
  //       text: item["Category-Text"] || item["Nav-Item-Text"],
  //       categoryValue: item["Category-Value"],
  //       isCategory: this.isCategory(item),
  //       isTopLevel: this.isTopLevelItem(item),
  //     })),
  //   );
  //
  //   // Check each category's children
  //   const categories = rootItems.filter((item) => this.isCategory(item));
  //   categories.forEach((category) => {
  //     const children = this.getChildrenForCategory(category["Category-Text"]!);
  //     console.log(
  //       `Children for "${category["Category-Text"]}":`,
  //       children.map((c) => c["Nav-Item-Text"]).filter(Boolean),
  //     );
  //   });
  // }
}
